import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Link
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Stadium as FieldIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import unifiedApi from '../utils/unifiedApi';
import { useAuth } from '../contexts/AuthContext';

const Venues = () => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const _mode = user ? user.role : 'public';

  // Helper function to open Google Maps with address
  const openGoogleMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  };

  useEffect(() => {
    const controller = new AbortController();
    
    const loadVenues = async () => {
      try {
        setLoading(true);
        setError(null);
        const venueData = await unifiedApi.get('/api/venues', { signal: controller.signal });
        // Only update state if the request wasn't aborted
        if (!controller.signal.aborted) {
          setVenues(venueData || []);
        }
      } catch (err) {
        // Only set error if the request wasn't aborted
        if (!controller.signal.aborted && err.name !== 'AbortError') {
          setError('Failed to load venues. Please try again.');
          console.error('Error loading venues:', err);
        }
      } finally {
        // Only update loading state if the request wasn't aborted
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadVenues();

    // Cleanup function to abort the request if component unmounts or effect re-runs
    return () => {
      controller.abort();
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <LocationIcon color="primary" />
          Venues & Fields
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover where our teams play across the organization
        </Typography>
      </Box>

      {/* Venues Grid */}
      {venues && venues.length > 0 ? (
        <Grid container spacing={3}>
          {venues.map((venue) => (
            <Grid item xs={12} md={6} lg={4} key={venue.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ flex: 1, p: 3 }}>
                  {/* Venue Header */}
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <LocationIcon sx={{ color: '#2E4156' }} />
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', color: '#2E4156' }}>
                      {venue.name}
                    </Typography>
                  </Stack>

                  {/* Address */}
                  {venue.address && (
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => openGoogleMaps(venue.address)}
                      sx={{
                        color: 'textSecondary',
                        textAlign: 'left',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#2E4156',
                        },
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                      }}
                      title="Open in Google Maps"
                    >
                      {venue.address}
                      <OpenInNewIcon sx={{ fontSize: '0.875rem', ml: 0.5 }} />
                    </Link>
                  )}

                  {/* Fields */}
                  {venue.fields && venue.fields.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <FieldIcon sx={{ color: '#2E4156', fontSize: '1.2rem' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2E4156' }}>
                          Fields ({venue.fields.length})
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {venue.fields.map((field) => (
                          <Chip
                            key={field.id}
                            label={field.name}
                            size="small"
                            variant="outlined"
                            onClick={(e) => e.preventDefault()}
                            sx={{
                              color: '#2E4156',
                              borderColor: '#2E4156',
                              cursor: 'default',
                              '&:hover': {
                                backgroundColor: 'rgba(46, 65, 86, 0.04)',
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </>
                  )}

                  {/* No Fields Message */}
                  {(!venue.fields || venue.fields.length === 0) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                        No fields configured for this venue
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box mt={4}>
          <Alert severity="info">
            No public venues are currently available for this organization.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default Venues;